import * as yup from "yup";

import { FeatureQueryStatus, FeatureType } from "src/clients/features-control";

export type ColumnExistsValidationContext = {
  availableColumns: string[];
  values?: {
    columns?: {
      features?: Array<{ feature_key?: string | null }>;
    };
  };
};

const NONE_VALUES = /^(None|none|null)$/;

const safeParse = (v: string): unknown => {
  try {
    return JSON.parse(
      v
        .replace(/\bTrue\b/g, "true")
        .replace(/\bFalse\b/g, "false")
        .replace(/\bNone\b/g, "null"),
    );
  } catch {
    return undefined;
  }
};

const TYPE_MATCHERS: Partial<Record<FeatureType, (v: string) => boolean>> = {
  [FeatureType.NUMBER]: (v) => v.length > 0 && Number.isFinite(Number(v)),
  [FeatureType.BOOLEAN]: (v) => /^(True|False|true|false)$/.test(v),
  [FeatureType.STRING]: (v) =>
    v.length >= 2 &&
    ((v.startsWith('"') && v.endsWith('"')) ||
      (v.startsWith("'") && v.endsWith("'"))),
  [FeatureType.ARRAY]: (v) => Array.isArray(safeParse(v)),
  [FeatureType.OBJECT]: (v) => {
    const parsed = safeParse(v);
    return (
      typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
    );
  },
};

const defaultValueTypeValidation = {
  name: "type-match",
  test: function (this: yup.TestContext, value: string | null | undefined) {
    if (!value) return true;
    const featureType = this.parent?.feature_type as FeatureType | undefined;
    if (!featureType) return true;
    const v = value.trim();
    if (NONE_VALUES.test(v)) return true;
    const matches = TYPE_MATCHERS[featureType];
    if (!matches || matches(v)) return true;
    return this.createError({
      message: `Default value doesn't match the column type (${featureType})`,
    });
  },
};

export const columnExistsValidation = {
  name: "column-exists",
  message: "Column name doesn't exist in query results. Update the value.",
  test: (
    value: string | null | undefined,
    context: yup.TestContext<yup.AnyObject>,
  ) => {
    if (!value) return true;
    const availableColumns = context.options.context?.availableColumns;
    // Skip validation when no columns are available (e.g. empty result set)
    if (!availableColumns?.length) return true;
    return availableColumns.includes(value);
  },
} as const;

export const schema = yup.object({
  query_name: yup
    .string()
    .nullable()
    .transform((value) => value ?? "")
    .required()
    .min(1),
  query: yup.string().required().min(1),
  columns: yup
    .object({
      entities: yup
        .array()
        .of(
          yup.object().shape({
            column_name: yup.string().when("entity_name", {
              is: (val: string) => !!val,
              then: (schema) =>
                schema
                  .required(
                    "Entity column is required. Run the query to map it.",
                  )
                  .test(columnExistsValidation),
              otherwise: (schema) => schema.optional(),
            }),
            entity_name: yup.string().optional(),
          }),
        )
        .max(1),
      features: yup
        .array()
        .of(
          yup.object().shape({
            column_name: yup
              .string()
              .transform((value) => value?.trim())
              .required("Column name is required")
              .test(columnExistsValidation),
            feature_key: yup
              .string()
              .transform((value) => value?.trim())
              .required("Feature key is required")
              .max(64, "Feature key must be at most 64 characters long")
              .matches(
                /^[a-zA-Z0-9_-]+$/,
                "Feature key can only contain letters, numbers, underscores, and hyphens",
              )
              .test(
                "unique-key",
                "Feature key must be unique",
                function (value) {
                  if (!value) return true;
                  const features =
                    this.options.context?.values?.columns?.features ?? [];
                  const count = features.filter(
                    (f: { feature_key?: string | null }) =>
                      f?.feature_key?.trim() === value,
                  ).length;
                  return count <= 1;
                },
              ),
            name: yup
              .string()
              .transform((value) => value?.trim())
              .required("Feature name is required"),
            feature_type: yup.mixed<FeatureType>().optional(),
            feature_default: yup
              .string()
              .nullable()
              .transform((value) => value ?? "")
              .required("Default value is required, set None if not applicable")
              .test(defaultValueTypeValidation),
            description: yup.string().nullable().optional(),
          }),
        )
        .min(1),
      grouping_keys: yup
        .array()
        .of(yup.string().required().test(columnExistsValidation))
        .max(1),
    })
    .required(),
  window_length: yup.number().required(),
  window_hop: yup.number().required(),
  end_datetime: yup.date().optional(),
});

export type QueryFormValues = yup.InferType<typeof schema>;

const DEFAULT_QUERY_TEMPLATE = `SELECT
  /* add your columns and aggregation functions here */
FROM
  /* add your table name here */
WHERE
    timestamp BETWEEN from_iso8601_timestamp(?) AND from_iso8601_timestamp(?)
  /* add additional filters here */
GROUP BY
  /* add your grouping columns here or remove if making a Global Feature */`;

export const TIMESTAMP_BETWEEN_PATTERN =
  /from_iso8601_timestamp\s*\(\s*\?\s*\)[\s\S]*from_iso8601_timestamp\s*\(\s*\?\s*\)/i;

export const TIMESTAMP_VALIDATION_MESSAGE =
  "To schedule your query on a recurring basis, include timestamp BETWEEN from_iso8601_timestamp(?) AND from_iso8601_timestamp(?) in your WHERE clause.";

export const defaultFormValues = {
  query_name: "",
  status: FeatureQueryStatus.INACTIVE,
  query: DEFAULT_QUERY_TEMPLATE,
  columns: {
    entities: [{ column_name: "", entity_name: "" }],
    grouping_keys: [],
    features: [],
  },
  window_length: undefined,
  window_hop: undefined,
};
